import { ArrowRight, Info, Share2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import AppEmptyState from "@/components/ui/AppEmptyState";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton, SkeletonRow } from "@/components/ui/skeleton";
import { getRealCjmTooltipText } from "@/lib/pilotageCollaborators";
import { formatDays, formatPilotageCount, formatPilotageCurrency, formatPilotagePercentDetail } from "@/lib/pilotageUi";

const MarginDetail = ({ row }) => {
  const isSalaried = ["CDI", "CDD", "ALTERNANT"].includes(String(row.contractType).toUpperCase());
  const periodMargin = row.marginWithIntermediaryPct;
  const hasFreeDays = row.freeDays > 0;
  const retainedDays = row.retainedDays ?? row.workedDays ?? (row.billableDays || 0);
  const expenses = row.expenses ?? 0;
  const realCost = row.realCost ?? ((row.collaboratorCost ?? row.consultantCost ?? 0) + expenses);
  const realMargin = row.realMargin ?? row.beneficeAnnuelEstime;
  const realMarginPct = row.realMarginPct ?? periodMargin;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-lenvia-blue hover:text-blue-300"
          aria-label={`Voir le détail financier de ${row.consultantLabel}`}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          Détail <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(24rem,calc(100vw-2rem))] border-white/10 bg-slate-950 p-0 text-slate-200 shadow-2xl"
        data-testid={`revenue-margin-detail-${row.missionId || row.collaborator_id}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-white/10 px-4 py-3">
          <p className="text-sm font-semibold text-white">Lecture de la rentabilité & marge</p>
          <p className="mt-1 text-xs text-slate-400">{row.consultantLabel} · {row.contractType || "Mission"}</p>
        </div>
        <div className="space-y-4 p-4 text-xs">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
            <dt className="text-slate-400">Marge théorique</dt>
            <dd className="text-right font-semibold text-white">{formatPilotagePercentDetail(row.theoreticalMarginPct)}</dd>
            <dt className="text-slate-400">Marge période</dt>
            <dd className="text-right font-semibold text-white">{formatPilotagePercentDetail(periodMargin)}</dd>
            <dt className="text-slate-400">Marge sans intermédiaire</dt>
            <dd className="text-right">{formatPilotagePercentDetail(row.marginWithoutIntermediaryPct)}</dd>
          </dl>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-3">
            <dt className="text-slate-400">TJM client</dt>
            <dd className="text-right font-medium text-white">{formatPilotageCurrency(row.tjmClient)}</dd>
            {row.tjmNet !== undefined && row.tjmNet !== null && row.tjmNet !== row.tjmClient ? (
              <>
                <dt className="text-slate-400">TJM net (après interm.)</dt>
                <dd className="text-right font-medium text-white">{formatPilotageCurrency(row.tjmNet)}</dd>
              </>
            ) : null}
            <dt className="text-slate-400">{isSalaried ? "Coût journalier" : "CJM consultant"}</dt>
            <dd className="text-right font-medium text-white">{formatPilotageCurrency(row.cjm)}</dd>
            <dt className="text-slate-400">Jours retenus</dt>
            <dd className="text-right font-medium text-white">{formatDays(retainedDays)}</dd>
            <dt className="text-slate-400">Jours travaillés</dt>
            <dd className="text-right">{formatDays(row.workedDays)}</dd>
            <dt className="text-slate-400">Jours facturés</dt>
            <dd className="text-right">{formatDays(row.billableDays)}</dd>
            <dt className="text-slate-400">Jours gratuits</dt>
            <dd className={`text-right font-medium ${hasFreeDays ? "text-amber-300" : "text-slate-300"}`}>{formatDays(row.freeDays)}</dd>
          </dl>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-3">
            <dt className="text-slate-400">CA période</dt>
            <dd className="text-right font-medium text-white">{formatPilotageCurrency(row.revenue ?? row.caAnnuelEstime)}</dd>
            <dt className="text-slate-400">Coût contractuel</dt>
            <dd className="text-right">{formatPilotageCurrency(row.collaboratorCost ?? row.consultantCost)}</dd>
            <dt className="text-slate-400">Frais collaborateur</dt>
            <dd className="text-right font-medium text-amber-200">{formatPilotageCurrency(expenses)}</dd>
            <dt className="font-medium text-slate-300">Coût réel</dt>
            <dd className="text-right font-semibold text-rose-200">{formatPilotageCurrency(realCost)}</dd>
            <dt className="font-medium text-slate-300">CJM RÉEL</dt>
            <dd className="text-right font-semibold text-white">
              {Number.isFinite(row.realCjm) ? `${formatPilotageCurrency(row.realCjm)}/j` : "—"}
            </dd>
            <dt className="text-slate-400">Marge avant frais</dt>
            <dd className="text-right">{formatPilotageCurrency(row.marginBeforeExpenses ?? ((row.revenue ?? row.caAnnuelEstime) - (row.collaboratorCost ?? row.consultantCost)))}</dd>
            <dt className="font-semibold text-white">Bénéfice / Marge réelle</dt>
            <dd className="text-right font-semibold text-white">{formatPilotageCurrency(realMargin)}</dd>
            <dt className="font-semibold text-white">Marge réelle (%)</dt>
            <dd className="text-right font-semibold text-emerald-400">{formatPilotagePercentDetail(realMarginPct)}</dd>
          </dl>

          {hasFreeDays ? (
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3">
              <p className="mb-2 font-semibold text-amber-200">Impact des {formatPilotageCount(row.freeDays)} jours gratuits</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                <dt className="text-slate-400">Coût supporté LENVIA</dt>
                <dd className="text-right font-medium text-amber-100">{formatPilotageCurrency(row.freeDaysCost)}</dd>
                <dt className="text-slate-400">CA non facturé</dt>
                <dd className="text-right font-medium text-amber-100">{formatPilotageCurrency(row.lostRevenue)}</dd>
              </dl>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MONTH_NAMES = {
  1: "janvier",
  2: "février",
  3: "mars",
  4: "avril",
  5: "mai",
  6: "juin",
  7: "juillet",
  8: "août",
  9: "septembre",
  10: "octobre",
  11: "novembre",
  12: "décembre",
};

export const resolveCollaboratorRevenueTarget = (row) => {
  if (row?.missionId) {
    return `/admin/pilotage/missions/${row.missionId}`;
  }

  const activeMissionIds = Array.isArray(row?.activeMissionIds)
    ? row.activeMissionIds.filter(Boolean)
    : [];

  if (activeMissionIds.length === 1) {
    return `/admin/pilotage/missions/${activeMissionIds[0]}`;
  }

  if (activeMissionIds.length > 1 && row?.collaborator_id) {
    return `/admin/pilotage/missions?collaborator=${encodeURIComponent(row.collaborator_id)}`;
  }

  return "";
};

const CollaboratorsRevenueTable = ({
  rows = [],
  isLoading = false,
  tableTestId = "pilotage-consultant-revenue",
  showViewMore = false,
  showStatus = true,
  year,
  month,
  viewMoreTo = "/admin/pilotage/collaborateurs/revenue",
  viewMoreLabel = "Voir plus",
}) => {
  const navigate = useNavigate();
  const normalizedRows = Array.isArray(rows) ? rows : [];

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid={tableTestId}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <Skeleton className="h-4 w-full bg-white/10" />
          </div>
        </div>
        <div
          className="space-y-3 rounded-xl border border-white/10 bg-white/[0.02] p-4"
          data-testid="pilotage-collaborators-revenue-table-skeleton"
        >
          {Array.from({ length: 5 }, (_, index) => (
            <SkeletonRow key={`collaborators-skeleton-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  if (normalizedRows.length === 0) {
    return (
      <AppEmptyState
        title="Aucune mission active pour le moment."
        description="Aucune ligne collaborateurs n'est disponible pour le moment."
        actionLabel="Créer une mission"
        onAction={() => navigate("/admin/pilotage/missions")}
        className="mt-2"
      />
    );
  }

  const monthlyTotal = normalizedRows.reduce(
    (total, row) => total + (Number.isFinite(row?.caMensuelEstime) ? row.caMensuelEstime : 0),
    0,
  );

  const resolveStatusToneClass = (status) => {
    const normalizedStatus = String(status || "").toUpperCase();

    if (normalizedStatus === "EN MISSION") {
      return "border-emerald-500/20 bg-emerald-500/10 text-emerald-300";
    }
    if (normalizedStatus === "INTERCONTRAT") {
      return "border-amber-500/20 bg-amber-500/10 text-amber-300";
    }
    if (normalizedStatus === "SORTI" || normalizedStatus === "INACTIF") {
      return "border-slate-500/20 bg-slate-500/10 text-slate-400";
    }

    return "border-slate-500/20 bg-slate-500/10 text-slate-300";
  };

  const monthLabel = month && MONTH_NAMES[month] ? MONTH_NAMES[month] : "";

  return (
    <div className="space-y-4" data-testid={tableTestId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <Share2 className="h-4 w-4 text-lenvia-blue" />
          <p className="text-sm font-medium text-slate-300">
            Total mensuel net {monthLabel && year ? `(${monthLabel} ${year})` : "affiché"} :
            {" "}
            <span className="font-semibold text-white">{formatPilotageCurrency(monthlyTotal)}</span>
          </p>
        </div>

        {showViewMore ? (
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
          >
            <Link to={viewMoreTo} data-testid="pilotage-collaborators-revenue-view-more">
              {viewMoreLabel}
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="w-full min-w-0 overflow-x-auto rounded-xl border border-white/10 bg-white/[0.02]">
        <table className="w-full min-w-[980px] table-fixed border-separate border-spacing-0 text-left text-sm leading-6 text-slate-300">
          <thead className="sticky top-0 z-10 bg-slate-950/95 text-slate-400 backdrop-blur">
            <tr>
              <th className="w-[14%] px-3 py-3 text-[11px] font-medium uppercase tracking-wider">Client</th>
              <th className="w-[14%] px-3 py-3 text-[11px] font-medium uppercase tracking-wider">Consultant</th>
              {showStatus ? (
                <th className="w-[8%] px-3 py-3 text-[11px] font-medium uppercase tracking-wider">Statut</th>
              ) : null}
              <th className="w-[7%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight">
                CJM<br />(€)
              </th>
              <th className="w-[7%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight">
                Frais {year || ""}<br />(€)
              </th>
              <th className="w-[10%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight">
                <span className="inline-flex items-center justify-end gap-1">
                  CJM RÉEL<br />(€)
                  <Info
                    className="h-3.5 w-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                    title="CJM réel = coût journalier du collaborateur augmenté des frais collaborateur imputables à la période, rapporté au nombre de jours retenus."
                  />
                </span>
              </th>
              <th className="w-[9%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight" data-testid="revenue-col-ca-mensuel">
                {monthLabel ? (
                  <>
                    <span className="sr-only">CA mensuel </span>
                    CA {monthLabel}<br />{year ? `${year} (€)` : "net (€)"}
                  </>
                ) : (
                  <>
                    CA mensuel<br />net (€)
                  </>
                )}
              </th>
              <th className="w-[9%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight" data-testid="revenue-col-ca-annuel">
                CA {year || "annuel"}<br />net (€)
              </th>
              <th className="w-[10%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight">
                Bénéfice<br />{year || "annuel"} (€)
              </th>
              <th className="w-[12%] px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider leading-tight">
                <span className="inline-flex items-center justify-end gap-1">
                  Marge période<br />(%)
                  <Info
                    className="h-3.5 w-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                    title="Rentabilité de la période après coût consultant, gratuité, frais et intermédiaire"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {normalizedRows.map((row, rowIndex) => {
              const target = resolveCollaboratorRevenueTarget(row);
              const isClickable = Boolean(target);
              const rowIdentifier = row.missionId || row.collaborator_id || row.collaborator_key;

              return (
                <tr
                  key={rowIdentifier || row.consultantLabel}
                  className={[
                    rowIndex % 2 === 0 ? "bg-white/[0.01]" : "bg-transparent",
                    isClickable ? "cursor-pointer transition-colors hover:bg-gray-50/10" : "opacity-90",
                  ].join(" ")}
                  data-testid={`pilotage-collaborators-revenue-row-${rowIdentifier || "unknown"}`}
                  title={isClickable ? undefined : "Mission indisponible"}
                  role={isClickable ? "button" : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={() => {
                    if (isClickable) {
                      navigate(target);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!isClickable) {
                      return;
                    }

                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(target);
                    }
                  }}
                >
                  <td className="px-3 py-4 align-top">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white" title={row.clientName || "—"}>
                        {row.clientName || "—"}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 py-4 align-top">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white" title={row.consultantLabel || "—"}>
                        {row.consultantLabel || "—"}
                      </p>
                    </div>
                  </td>
                  {showStatus ? (
                    <td className="px-3 py-4 align-top">
                      <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${resolveStatusToneClass(row.status)}`}>
                        {row.status || "—"}
                      </span>
                    </td>
                  ) : null}
                  <td
                    className="px-3 py-4 text-right tabular-nums align-top truncate text-slate-300"
                    title={`CJM contractuel : ${formatPilotageCurrency(row.cjm)}`}
                    data-testid={`revenue-cjm-${rowIdentifier || "unknown"}`}
                  >
                    {formatPilotageCurrency(row.cjm)}
                  </td>
                  <td
                    className="px-3 py-4 text-right tabular-nums align-top truncate font-medium text-amber-200"
                    title={`Frais collaborateur : ${formatPilotageCurrency(row.expenses ?? 0)}`}
                    data-testid={`revenue-expenses-${rowIdentifier || "unknown"}`}
                  >
                    {formatPilotageCurrency(row.expenses ?? 0)}
                  </td>
                  <td
                    className="px-3 py-4 text-right tabular-nums align-top"
                    title={getRealCjmTooltipText(row)}
                    data-testid={`revenue-real-cjm-${rowIdentifier || "unknown"}`}
                  >
                    <div className="font-semibold text-white">
                      {Number.isFinite(row.realCjm) ? formatPilotageCurrency(row.realCjm) : "—"}
                    </div>
                    {Number.isFinite(row.realCjm) && Number.isFinite(row.cjm) && row.realCjm > row.cjm ? (
                      <div className="text-[11px] font-medium text-amber-300">
                        +{formatPilotageCurrency(row.realCjm - row.cjm)}/j
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-4 text-right font-semibold tabular-nums text-white align-top truncate" title={formatPilotageCurrency(row.caMensuelEstime)}>
                    {formatPilotageCurrency(row.caMensuelEstime)}
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums align-top truncate" title={formatPilotageCurrency(row.caAnnuelEstime)}>
                    {formatPilotageCurrency(row.caAnnuelEstime)}
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums align-top truncate" title={formatPilotageCurrency(row.beneficeAnnuelEstime)}>
                    {formatPilotageCurrency(row.beneficeAnnuelEstime)}
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums align-top">
                    <div className="font-semibold text-white">{formatPilotagePercentDetail(row.marginWithIntermediaryPct)}</div>
                    {Number.isFinite(row.marginGapPctPoints) && Math.abs(row.marginGapPctPoints) >= 0.01 ? (
                      <div className="mt-1 text-[11px] font-medium text-amber-300">
                        {row.marginGapPctPoints > 0 ? "+" : ""}{row.marginGapPctPoints.toFixed(2).replace(".", ",")} pts
                        {row.freeDays > 0 ? ` · ${formatPilotageCount(row.freeDays)} j gratuit${row.freeDays > 1 ? "s" : ""}` : ""}
                      </div>
                    ) : null}
                    <MarginDetail row={row} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CollaboratorsRevenueTable;
